import { LifeThemeConfig, LifeThemeId, LifeThemeQuestion } from '../types/advancedLifeAnalysis';

export const LIFE_THEMES_REGISTRY: Record<LifeThemeId, LifeThemeConfig> = {
  career_job: {
    id: 'career_job',
    nameEn: 'Career & Professional Growth',
    nameHi: 'कार्यक्षेत्र, पदोन्नति एवं आजीविका',
    iconName: 'Briefcase',
    descriptionEn: 'Vedic career trajectory, promotion timings, service vs enterprise suitability, authority & rank',
    descriptionHi: 'दशम भाव, दशमांश चक्र, षडबल एवं राजयोग आधारित संपूर्ण करियर विश्लेषण',
    primaryHouses: [10, 6, 1],
    secondaryHouses: [2, 11, 7],
    naturalKarakas: ['Sun', 'Mercury', 'Saturn', 'Jupiter'],
    relevantVargas: ['D1', 'D10', 'D9'],
    classicalTextReference: 'BPHS Ch. 11, 19 (Karma Bhava) & Saravali Ch. 34 (Rajayoga)',
    questions: [
      {
        id: 'career_timing_promotion_job_change',
        themeId: 'career_job',
        questionEn: 'When will I get a new job, promotion, or major career breakthrough?',
        questionHi: 'मुझे नई नौकरी, पदोन्नति या करियर में बड़ा बदलाव कब मिलेगा?',
        category: 'TIMING',
        descriptionEn: 'Calculates active Dasha lords, 10th/6th house transit activations, and double-transit confluence',
        descriptionHi: 'दशम एवं षष्ठ भाव की दशा, गोचर व द्वि-गोचर के आधार पर सटीक समय-विंडो की गणना',
        primaryHouses: [10, 6, 11],
        secondaryHouses: [1, 2, 9],
        primaryKarakas: ['Sun', 'Saturn'],
        secondaryKarakas: ['Mercury', 'Jupiter'],
        relevantVargas: ['D1', 'D10', 'D9'],
        timingRelevant: true
      },
      {
        id: 'career_govt_vs_private',
        themeId: 'career_job',
        questionEn: 'Is government service, PSU, or private corporate sector more favorable for me?',
        questionHi: 'क्या मेरे लिए सरकारी नौकरी, प्रशासनिक पद अथवा निजी कॉर्पोरेट क्षेत्र अधिक अनुकूल है?',
        category: 'COMPARISON',
        descriptionEn: 'Evaluates Sun/Mars connection to 10th/1st house and Amatyakaraka strength in D1 & D10',
        descriptionHi: 'सूर्य, मंगल एवं आमात्यकारक की दशम/लग्न भाव व दशमांश में शक्ति का तुलनात्मक विश्लेषण',
        primaryHouses: [10, 1, 6],
        secondaryHouses: [5, 9, 11],
        primaryKarakas: ['Sun', 'Mars', 'Jupiter'],
        secondaryKarakas: ['Saturn', 'Mercury'],
        relevantVargas: ['D1', 'D10'],
        timingRelevant: false
      },
      {
        id: 'career_long_term_peak',
        themeId: 'career_job',
        questionEn: 'What is my long-term career peak and ultimate professional status?',
        questionHi: 'मेरा दीर्घकालिक करियर चरम और जीवन में सर्वोच्च पद/प्रतिष्ठा क्या होगी?',
        category: 'PROMISE',
        descriptionEn: 'Synthesizes D10 Lagna & 10th house, Raja Yogas, and Shadbala rank of 10th Lord',
        descriptionHi: 'दशमांश लग्न, दशमेश का षडबल, दिगबल एवं प्रमुख राजयोगों का विस्तृत फलादेश',
        primaryHouses: [10, 1, 9],
        secondaryHouses: [2, 5, 11],
        primaryKarakas: ['Sun', 'Jupiter', 'Saturn'],
        secondaryKarakas: ['Mercury'],
        relevantVargas: ['D1', 'D10', 'D9'],
        timingRelevant: true
      },
      {
        id: 'career_switch_or_stay',
        themeId: 'career_job',
        questionEn: 'Should I switch my current job now or wait for a more auspicious window?',
        questionHi: 'क्या मुझे वर्तमान नौकरी अभी बदलनी चाहिए अथवा शुभ समय की प्रतीक्षा करनी चाहिए?',
        category: 'DIAGNOSTIC',
        descriptionEn: 'Evaluates current Ashtakavarga transit scores, 8th/12th house vulnerability, and sub-dasha',
        descriptionHi: 'वर्तमान अष्टकवर्ग गोचर अंक, अष्टम/द्वादश भाव का प्रभाव व उपदशा की अनुकूलता',
        primaryHouses: [6, 10, 8],
        secondaryHouses: [12, 3, 11],
        primaryKarakas: ['Saturn', 'Mercury'],
        secondaryKarakas: ['Sun'],
        relevantVargas: ['D1', 'D10'],
        timingRelevant: true
      }
    ]
  },

  marriage_relationship: {
    id: 'marriage_relationship',
    nameEn: 'Marriage & Relationships',
    nameHi: 'विवाह, दाम्पत्य सुख एवं संबंध',
    iconName: 'Heart',
    descriptionEn: '7th Bhava, Navamsha D9, Venus/Jupiter dignity, Vivaha Muhurta & marital harmony promise',
    descriptionHi: 'सप्तम भाव, नवमांश चक्र, शुक्र/गुरु की स्थिति व विवाह समय निर्धारण',
    primaryHouses: [7, 2, 11],
    secondaryHouses: [1, 4, 8, 12],
    naturalKarakas: ['Venus', 'Jupiter', 'Mars'],
    relevantVargas: ['D1', 'D9'],
    classicalTextReference: 'BPHS Ch. 16 (Kalatra Bhava) & Phaladeepika Ch. 10',
    questions: [
      {
        id: 'marriage_timing_window',
        themeId: 'marriage_relationship',
        questionEn: 'When is the most auspicious timing window for my marriage?',
        questionHi: 'मेरे विवाह का सबसे शुभ और प्रबल योग समय कब बनेगा?',
        category: 'TIMING',
        descriptionEn: 'Cross-analyzes 7th/2nd Lord Dasha with Jupiter/Saturn double transit over 7th/1st house and D9 Lagna',
        descriptionHi: 'सप्तमेश/द्वितीयेश की दशा और गुरु/शनि के सप्तम/लग्न पर द्वि-गोचर का संमिलन',
        primaryHouses: [7, 2, 11],
        secondaryHouses: [1, 5, 9],
        primaryKarakas: ['Venus', 'Jupiter'],
        secondaryKarakas: ['Moon'],
        relevantVargas: ['D1', 'D9'],
        timingRelevant: true
      },
      {
        id: 'marriage_spouse_characteristics',
        themeId: 'marriage_relationship',
        questionEn: 'What are the nature, background, and characteristics of my future spouse?',
        questionHi: 'मेरे भावी जीवनसाथी का स्वभाव, व्यक्तित्व, रूप-रंग और दिशा कैसी होगी?',
        category: 'PROMISE',
        descriptionEn: 'Evaluates 7th house sign, occupants, 7th Lord navamsha dispositor, and Dara Karaka',
        descriptionHi: 'सप्तम राशि, उसमें स्थित ग्रह, दाराकारक एवं नवमांश सप्तमेश का संयुक्त स्वरूप',
        primaryHouses: [7, 1],
        secondaryHouses: [2, 4, 9],
        primaryKarakas: ['Venus', 'Jupiter'],
        secondaryKarakas: ['Moon', 'Mercury'],
        relevantVargas: ['D1', 'D9'],
        timingRelevant: false
      },
      {
        id: 'marriage_harmony_longevity',
        themeId: 'marriage_relationship',
        questionEn: 'How will be the harmony, mutual understanding, and longevity of married life?',
        questionHi: 'दाम्पत्य जीवन में आपसी तालमेल, सुख-शांति एवं स्थायित्व कैसा रहेगा?',
        category: 'DIAGNOSTIC',
        descriptionEn: 'Examines 2nd/4th/7th/8th house balance, Upapada Lagna, and Navamsha Mangal/Ketu doshas',
        descriptionHi: 'कुटुंब (२), सुख (४), जाया (७) एवं आयु (८) भाव, उपपद लग्न व नवमांश की शुद्धि',
        primaryHouses: [7, 4, 2],
        secondaryHouses: [8, 12, 1],
        primaryKarakas: ['Venus', 'Jupiter'],
        secondaryKarakas: ['Mars', 'Ketu'],
        relevantVargas: ['D1', 'D9'],
        timingRelevant: true
      },
      {
        id: 'marriage_obstacles_remedies',
        themeId: 'marriage_relationship',
        questionEn: 'Are there any planetary delay factors or relationship obstacles, and what are the remedies?',
        questionHi: 'विवाह में विलंब या बाधा के क्या शास्त्रीय कारण हैं और इनके सटीक उपाय क्या हैं?',
        category: 'REMEDIAL',
        descriptionEn: 'Identifies Saturn/Rahu aspect on 7th, 7th lord debilitation, and prescribes Vedic remedies',
        descriptionHi: 'शनि/राहु का सप्तम पर प्रभाव, सप्तमेश नीचत्व एवं शास्त्रीय व्रत-मंत्र-दान विधान',
        primaryHouses: [7, 8, 12],
        secondaryHouses: [6, 2],
        primaryKarakas: ['Saturn', 'Rahu', 'Venus'],
        secondaryKarakas: ['Mars'],
        relevantVargas: ['D1', 'D9'],
        timingRelevant: true
      }
    ]
  },

  wealth_finance: {
    id: 'wealth_finance',
    nameEn: 'Wealth & Financial Accumulation',
    nameHi: 'धन, समृद्धि, कोष एवं वित्तीय वृद्धि',
    iconName: 'Coins',
    descriptionEn: '2nd Bhava (Kosha), 11th Bhava (Labha), Indu Lagna, D2 Hora chart & Dhana Yogas',
    descriptionHi: 'द्वितीय भाव, एकादश भाव, इंदु लग्न, होरा चक्र (D2) एवं धन योग विश्लेषण',
    primaryHouses: [2, 11, 9],
    secondaryHouses: [1, 5, 4, 8],
    naturalKarakas: ['Jupiter', 'Venus', 'Mercury'],
    relevantVargas: ['D1', 'D2', 'D9'],
    classicalTextReference: 'BPHS Ch. 12 (Dhana Bhava) & Saravali Ch. 35',
    questions: [
      {
        id: 'wealth_primary_sources_magnitude',
        themeId: 'wealth_finance',
        questionEn: 'What are my primary sources of wealth and total financial capacity?',
        questionHi: 'मेरे जीवन में धन अर्जन के प्रमुख स्रोत और कुल आर्थिक स्तर क्या रहेगा?',
        category: 'PROMISE',
        descriptionEn: 'Synthesizes Dhana Yoga counts, 2nd & 11th Lord strength, and D2 Hora balance',
        descriptionHi: 'धन योगों की संख्या, द्वितीयेश/एकादशेश की स्थिति व होरा चक्र (D2) का समग्र फलादेश',
        primaryHouses: [2, 11, 9],
        secondaryHouses: [1, 5],
        primaryKarakas: ['Jupiter', 'Venus'],
        secondaryKarakas: ['Mercury'],
        relevantVargas: ['D1', 'D2', 'D9'],
        timingRelevant: false
      },
      {
        id: 'wealth_timing_peak_inflow',
        themeId: 'wealth_finance',
        questionEn: 'When will my major financial inflow, windfalls, and wealth accumulation peak?',
        questionHi: 'मेरे जीवन में सर्वाधिक धन लाभ, आर्थिक समृद्धि और कोष वृद्धि का काल कब आएगा?',
        category: 'TIMING',
        descriptionEn: 'Evaluates Dasha of Indu Lagna Lord, 2nd/11th Lord periods, and transit across SAV 30+ houses',
        descriptionHi: 'इंदु लग्नेश की दशा, द्वितीयेश/एकादशेश की अंतरदशा एवं उच्च सर्वाष्टकवर्ग गोचर का समय',
        primaryHouses: [11, 2, 9],
        secondaryHouses: [1, 5, 10],
        primaryKarakas: ['Jupiter', 'Mercury'],
        secondaryKarakas: ['Venus'],
        relevantVargas: ['D1', 'D2'],
        timingRelevant: true
      },
      {
        id: 'wealth_loss_debt_risks',
        themeId: 'wealth_finance',
        questionEn: 'Are there risks of sudden financial loss, debts, or wasteful expenditure?',
        questionHi: 'क्या अचानक धन हानि, ऋण (कर्ज) या व्यर्थ खर्च के योग हैं, इनसे कैसे बचें?',
        category: 'DIAGNOSTIC',
        descriptionEn: 'Examines 12th (Vyaya) & 6th (Rina) house afflictions, Daridra Yogas, and afflicted 2nd Lord',
        descriptionHi: 'द्वादश (व्यय) एवं षष्ठ (ऋण) भाव, दरिद्र योग और द्वितीयेश पर पापी ग्रहों की दृष्टि',
        primaryHouses: [12, 6, 8],
        secondaryHouses: [2, 11],
        primaryKarakas: ['Rahu', 'Mars', 'Saturn'],
        secondaryKarakas: ['Mercury'],
        relevantVargas: ['D1', 'D2'],
        timingRelevant: true
      }
    ]
  },

  house_property: {
    id: 'house_property',
    nameEn: 'House, Real Estate & Land',
    nameHi: 'भूमि, भवन, अचल संपत्ति एवं गृह निर्माण',
    iconName: 'Home',
    descriptionEn: '4th Bhava (Sukha/Griha), Mars (Bhumi Karaka), Saturn (Vastu), D4 Chaturthamsha',
    descriptionHi: 'चतुर्थ भाव, मंगल (भूमि कारक), शनि एवं चतुर्थांश चक्र (D4) आधारित संपत्ति विश्लेषण',
    primaryHouses: [4, 11, 1],
    secondaryHouses: [2, 9, 8],
    naturalKarakas: ['Mars', 'Saturn', 'Venus', 'Jupiter'],
    relevantVargas: ['D1', 'D4', 'D16'],
    classicalTextReference: 'BPHS Ch. 14 (Sukha/Vahana/Griha Bhava)',
    questions: [
      {
        id: 'property_timing_buy_construct',
        themeId: 'house_property',
        questionEn: 'When will I purchase or construct my own dream house/flat?',
        questionHi: 'मेरा अपना घर, फ्लैट या भवन निर्माण का शुभ योग कब बनेगा?',
        category: 'TIMING',
        descriptionEn: 'Calculates 4th Lord Dasha, Mars transit across 4th/11th house, and D4 Lagna Lord activation',
        descriptionHi: 'चतुर्थेश की दशा, मंगल का शुभ गोचर एवं चतुर्थांश चक्र (D4) में संपत्ति प्रदाता समय',
        primaryHouses: [4, 11, 1],
        secondaryHouses: [2, 9],
        primaryKarakas: ['Mars', 'Jupiter'],
        secondaryKarakas: ['Venus', 'Saturn'],
        relevantVargas: ['D1', 'D4'],
        timingRelevant: true
      },
      {
        id: 'property_investment_suitability',
        themeId: 'house_property',
        questionEn: 'Is real estate and land investment highly lucrative for my chart?',
        questionHi: 'क्या रियल एस्टेट, प्लॉट या भूमि में निवेश मेरे लिए अत्यंत लाभकारी रहेगा?',
        category: 'PROMISE',
        descriptionEn: 'Evaluates Mars dignity in D1 & D4, 4th Lord strength in Shadbala, and 4th house SAV points',
        descriptionHi: 'मंगल की D1 व D4 में स्थिति, चतुर्थेश का षडबल एवं चतुर्थ भाव के सर्वाष्टकवर्ग बिंदु',
        primaryHouses: [4, 11, 2],
        secondaryHouses: [1, 9],
        primaryKarakas: ['Mars', 'Mercury'],
        secondaryKarakas: ['Saturn'],
        relevantVargas: ['D1', 'D4'],
        timingRelevant: false
      }
    ]
  },

  vehicle: {
    id: 'vehicle',
    nameEn: 'Vehicle & Conveyance',
    nameHi: 'वाहन सुख एवं गतिशीलता',
    iconName: 'Car',
    descriptionEn: '4th Bhava (Vahana), Venus (Vahana Karaka), D16 Shodashamsha & mechanical safety',
    descriptionHi: 'चतुर्थ भाव, शुक्र (वाहन कारक) एवं षोडशांश चक्र (D16) से वाहन सुख का विश्लेषण',
    primaryHouses: [4, 11],
    secondaryHouses: [1, 2, 9],
    naturalKarakas: ['Venus', 'Moon', 'Mars'],
    relevantVargas: ['D1', 'D16'],
    classicalTextReference: 'BPHS Ch. 14 & Saravali Ch. 27',
    questions: [
      {
        id: 'vehicle_timing_purchase',
        themeId: 'vehicle',
        questionEn: 'When is the most auspicious time to buy a new car/vehicle?',
        questionHi: 'नई कार या वाहन खरीदने का सबसे शुभ मुहूर्त काल कब है?',
        category: 'TIMING',
        descriptionEn: 'Evaluates 4th house Lord & Venus Dasha with positive transit over 4th house',
        descriptionHi: 'चतुर्थेश व शुक्र की दशा तथा चतुर्थ भाव पर शुभ ग्रहों के गोचर की गणना',
        primaryHouses: [4, 11],
        secondaryHouses: [1, 2],
        primaryKarakas: ['Venus', 'Moon'],
        secondaryKarakas: ['Mars'],
        relevantVargas: ['D1', 'D16'],
        timingRelevant: true
      },
      {
        id: 'vehicle_comfort_safety',
        themeId: 'vehicle',
        questionEn: 'What type of vehicle brings maximum prosperity, comfort, and safety?',
        questionHi: 'किस प्रकार का वाहन और रंग मेरे लिए सबसे अधिक शुभ और सुरक्षित रहेगा?',
        category: 'DIAGNOSTIC',
        descriptionEn: 'Synthesizes 4th house sign element, Venus planetary ray, and D16 Shodashamsha',
        descriptionHi: 'चतुर्थ भाव का तत्व, शुक्र की शुभता एवं षोडशांश चक्र के आधार पर वाहन परामर्श',
        primaryHouses: [4, 1],
        secondaryHouses: [8, 12],
        primaryKarakas: ['Venus', 'Mars'],
        secondaryKarakas: ['Saturn'],
        relevantVargas: ['D1', 'D16'],
        timingRelevant: false
      }
    ]
  },

  children: {
    id: 'children',
    nameEn: 'Children & Progeny',
    nameHi: 'संतान सुख, वंश वृद्धि एवं संतति प्रगति',
    iconName: 'Baby',
    descriptionEn: '5th Bhava (Suta/Putra), Jupiter (Putrakaraka), D7 Saptamsha, Beeja/Kshetra Sphuta',
    descriptionHi: 'पंचम भाव, बृहस्पति (संतान कारक), सप्तमांश चक्र (D7) एवं संतान सुख विश्लेषण',
    primaryHouses: [5, 9, 2],
    secondaryHouses: [1, 11, 7],
    naturalKarakas: ['Jupiter', 'Mars', 'Venus'],
    relevantVargas: ['D1', 'D7', 'D9'],
    classicalTextReference: 'BPHS Ch. 15 (Suta Bhava) & Phaladeepika Ch. 12',
    questions: [
      {
        id: 'children_timing_conception_birth',
        themeId: 'children',
        questionEn: 'When is the most favorable timing for conception and childbirth?',
        questionHi: 'संतान प्राप्ति और गर्भाधान के लिए सबसे अनुकूल व शुभ समय कब है?',
        category: 'TIMING',
        descriptionEn: 'Calculates 5th/9th Lord Dasha, Jupiter transit aspecting 5th/9th house, and D7 Lagna alignment',
        descriptionHi: 'पंचमेश/नवमेश की दशा, गुरु का पंचम/नवम पर गोचरीय प्रभाव और D7 की अनुकूलता',
        primaryHouses: [5, 9, 11],
        secondaryHouses: [1, 2],
        primaryKarakas: ['Jupiter', 'Moon'],
        secondaryKarakas: ['Sun', 'Venus'],
        relevantVargas: ['D1', 'D7'],
        timingRelevant: true
      },
      {
        id: 'children_prosperity_education',
        themeId: 'children',
        questionEn: 'How will be the prosperity, intellect, and future of my children?',
        questionHi: 'मेरी संतान का भविष्य, विद्या, संस्कार और जीवन में उन्नति कैसी रहेगी?',
        category: 'PROMISE',
        descriptionEn: 'Examines 5th house sign, 5th Lord dignity in D7 Saptamsha, and Jupiter placement',
        descriptionHi: 'पंचम भाव, सप्तमांश में पंचमेश की स्थिति एवं गुरु के शुभ प्रभाव का अध्ययन',
        primaryHouses: [5, 9, 1],
        secondaryHouses: [10, 11],
        primaryKarakas: ['Jupiter', 'Mercury'],
        secondaryKarakas: ['Sun'],
        relevantVargas: ['D1', 'D7', 'D9'],
        timingRelevant: false
      },
      {
        id: 'children_education_career_support',
        themeId: 'children',
        questionEn: 'How can I best support my children’s academic stream, higher education, and career settlement?',
        questionHi: 'संतान की उच्च शिक्षा, करियर दिशा, विद्या एवं जीवन में सफलता हेतु क्या योग हैं?',
        category: 'PROMISE',
        descriptionEn: 'Evaluates 5th lord connection to 9th/10th houses, Mercury-Jupiter Vidyakaraka strength, and D7 planetary placements',
        descriptionHi: 'पंचमेश का नवम/दशम भाव से संबंध, बुध-गुरु विद्याकारक बल एवं सप्तमांश (D7) में शुभ ग्रहों की स्थिति',
        primaryHouses: [5, 9, 10],
        secondaryHouses: [2, 4, 11],
        primaryKarakas: ['Jupiter', 'Mercury', 'Sun'],
        secondaryKarakas: ['Venus'],
        relevantVargas: ['D1', 'D7', 'D24'],
        timingRelevant: true
      }
    ]
  },

  education: {
    id: 'education',
    nameEn: 'Education & Higher Knowledge',
    nameHi: 'विद्या, उच्च शिक्षा एवं प्रतियोगी परीक्षा',
    iconName: 'GraduationCap',
    descriptionEn: '4th Bhava (Foundational learning), 5th (Intellect/Buddhi), 9th (Higher research), D24 Chaturvimshamsha',
    descriptionHi: 'चतुर्थ (विद्या), पंचम (बुद्धि), नवम (उच्च ज्ञान) एवं चतुर्विंशांश चक्र (D24)',
    primaryHouses: [5, 4, 9],
    secondaryHouses: [1, 2, 10, 6],
    naturalKarakas: ['Mercury', 'Jupiter', 'Sun', 'Moon'],
    relevantVargas: ['D1', 'D24', 'D9'],
    classicalTextReference: 'BPHS Ch. 14, 15 & Jaimini Sutras (Karaka Dashamsha)',
    questions: [
      {
        id: 'education_field_stream_selection',
        themeId: 'education',
        questionEn: 'Which academic field/stream is most naturally aligned with my intellect?',
        questionHi: 'मेरी कुंडली के अनुसार कौन सा अध्ययन क्षेत्र (इंजीनियरिंग, चिकित्सा, विधि, वाणिज्य, कला) सर्वोत्तम है?',
        category: 'COMPARISON',
        descriptionEn: 'Synthesizes 5th Lord, Mercury/Jupiter dignity, and D24 Siddhamsa planet strength',
        descriptionHi: 'पंचमेश, बुध/गुरु की स्थिति व चतुर्विंशांश चक्र (D24) में विद्या-कारक ग्रहों का फलादेश',
        primaryHouses: [5, 4, 9],
        secondaryHouses: [10, 1],
        primaryKarakas: ['Mercury', 'Jupiter'],
        secondaryKarakas: ['Sun', 'Mars', 'Venus'],
        relevantVargas: ['D1', 'D24'],
        timingRelevant: false
      },
      {
        id: 'education_competitive_exam_timing',
        themeId: 'education',
        questionEn: 'When will I clear competitive examinations or prestigious entrance tests?',
        questionHi: 'प्रतियोगी परीक्षाओं (UPSC, IIT, NEET, CA) में सफलता का प्रबल योग कब बनेगा?',
        category: 'TIMING',
        descriptionEn: 'Evaluates 6th house (competition), 5th/10th Lord Dasha, and transits over SAV 28+ houses',
        descriptionHi: 'षष्ठ भाव (प्रतियोगिता), पंचमेश/दशमेश की दशा एवं शुभ गोचर का समय निर्धारण',
        primaryHouses: [6, 5, 10],
        secondaryHouses: [1, 9, 11],
        primaryKarakas: ['Mercury', 'Sun', 'Mars'],
        secondaryKarakas: ['Jupiter'],
        relevantVargas: ['D1', 'D24', 'D10'],
        timingRelevant: true
      }
    ]
  },

  health_vitality: {
    id: 'health_vitality',
    nameEn: 'Health, Vitality & Longevity',
    nameHi: 'स्वास्थ्य, जीवन शक्ति एवं दीर्घायु',
    iconName: 'Activity',
    descriptionEn: '1st Bhava (Tanu/Immunity), 6th (Roga), 8th (Ayur/Longevity), 22nd Drekkana, D30 Trishamsha',
    descriptionHi: 'प्रथम भाव (आरोग्य), षष्ठ (रोग), अष्टम (आयु), २२वां द्रेष्काण एवं त्रिंशांश चक्र (D30)',
    primaryHouses: [1, 6, 8],
    secondaryHouses: [12, 2, 7],
    naturalKarakas: ['Sun', 'Mars', 'Saturn', 'Moon'],
    relevantVargas: ['D1', 'D3', 'D30'],
    classicalTextReference: 'BPHS Ch. 13 (Tanu/Roga) & Saravali Ch. 20 (Arishta)',
    questions: [
      {
        id: 'health_constitutional_strengths_vulnerabilities',
        themeId: 'health_vitality',
        questionEn: 'What are my constitutional body strengths and vulnerable health areas?',
        questionHi: 'मेरी शारीरिक प्रकृति (वात, पित्त, कफ) कैसी है और किन अंगों का विशेष ध्यान रखना चाहिए?',
        category: 'DIAGNOSTIC',
        descriptionEn: 'Analyzes Lagna Lord, Sun/Moon strength, 6th house signs, and 22nd Drekkana Kharesh',
        descriptionHi: 'लग्नेश, सूर्य/चंद्र बल, षष्ठ भाव की राशि एवं २२वें द्रेष्काण के स्वामी का स्वास्थ्य परीक्षण',
        primaryHouses: [1, 6, 8],
        secondaryHouses: [12, 2],
        primaryKarakas: ['Sun', 'Moon'],
        secondaryKarakas: ['Mars', 'Saturn'],
        relevantVargas: ['D1', 'D3', 'D30'],
        timingRelevant: false
      },
      {
        id: 'health_timing_vigilance_recovery',
        themeId: 'health_vitality',
        questionEn: 'Which upcoming Dasha or transit periods require extra health vigilance and care?',
        questionHi: 'आगामी समय में स्वास्थ्य के प्रति कब विशेष सतर्कता बरतनी होगी और आरोग्य लाभ कब होगा?',
        category: 'TIMING',
        descriptionEn: 'Calculates 6th/8th Lord sub-periods, Saturn transit over natal Moon/Sun, and D30 afflictions',
        descriptionHi: 'षष्ठेश/अष्टमेश की अंतरदशा, शनि का गोचर और त्रिंशांश में ग्रह स्थिति का विश्लेषण',
        primaryHouses: [6, 8, 1],
        secondaryHouses: [12, 7],
        primaryKarakas: ['Saturn', 'Mars'],
        secondaryKarakas: ['Sun', 'Rahu'],
        relevantVargas: ['D1', 'D30'],
        timingRelevant: true
      }
    ]
  },

  business: {
    id: 'business',
    nameEn: 'Business & Entrepreneurship',
    nameHi: 'व्यापार, उद्यम, स्वतंत्र व्यवसाय एवं साझेदारी',
    iconName: 'TrendingUp',
    descriptionEn: '7th Bhava (Trade/Partnership), 3rd (Initiative), 10th (Commerce), 11th (Profits), Mercury',
    descriptionHi: 'सप्तम भाव (व्यापार), तृतीय (उद्यम), एकादश (लाभ), बुध व दशमांश आधारित विश्लेषण',
    primaryHouses: [7, 10, 11],
    secondaryHouses: [3, 2, 9, 1],
    naturalKarakas: ['Mercury', 'Jupiter', 'Sun', 'Mars'],
    relevantVargas: ['D1', 'D10', 'D2'],
    classicalTextReference: 'BPHS Ch. 16 & Phaladeepika Ch. 15',
    questions: [
      {
        id: 'business_solo_vs_partnership',
        themeId: 'business',
        questionEn: 'Is independent sole proprietorship or business partnership more profitable for me?',
        questionHi: 'क्या मेरे लिए एकल स्वतंत्र व्यवसाय उत्तम रहेगा अथवा साझेदारी (पार्टनरशिप) अधिक फलदायी होगी?',
        category: 'COMPARISON',
        descriptionEn: 'Evaluates 7th house dignity, 7th Lord vs 1st Lord strength, and Jaimini Dara Karaka',
        descriptionHi: 'सप्तमेश व लग्नेश का परस्पर संबंध, सप्तम भाव के शुभ-अशुभ ग्रह एवं साझेदारी की सफलता',
        primaryHouses: [7, 1, 11],
        secondaryHouses: [3, 2],
        primaryKarakas: ['Mercury', 'Jupiter'],
        secondaryKarakas: ['Sun', 'Venus'],
        relevantVargas: ['D1', 'D10'],
        timingRelevant: false
      },
      {
        id: 'business_timing_launch_expansion',
        themeId: 'business',
        questionEn: 'When is the best astrological window to launch a new venture or expand business?',
        questionHi: 'नया व्यापार आरंभ करने या व्यवसाय विस्तार के लिए सर्वश्रेष्ठ शुभ काल कब है?',
        category: 'TIMING',
        descriptionEn: 'Calculates 7th/11th Lord Dasha, Jupiter transit across 10th/11th house, and SAV transit score',
        descriptionHi: 'सप्तमेश/एकादशेश की अनुकूल दशा, गुरु का दशम/एकादश में गोचर व उच्च सर्वाष्टकवर्ग काल',
        primaryHouses: [7, 11, 10],
        secondaryHouses: [3, 2, 9],
        primaryKarakas: ['Mercury', 'Jupiter'],
        secondaryKarakas: ['Sun'],
        relevantVargas: ['D1', 'D10'],
        timingRelevant: true
      }
    ]
  },

  foreign_travel: {
    id: 'foreign_travel',
    nameEn: 'Foreign Travel & Settlement',
    nameHi: 'विदेश यात्रा, विदेशी नागरिकता एवं परदेस वास',
    iconName: 'Globe',
    descriptionEn: '12th Bhava (Foreign Lands/Sthana Tyaga), 9th (Long Journeys), 3rd (Short Travel), Rahu/Moon',
    descriptionHi: 'द्वादश भाव (विदेश वास), नवम भाव (दीर्घ यात्रा), राहु व चंद्र आधारित विदेश योग विश्लेषण',
    primaryHouses: [12, 9, 3],
    secondaryHouses: [1, 7, 4],
    naturalKarakas: ['Rahu', 'Moon', 'Saturn', 'Jupiter'],
    relevantVargas: ['D1', 'D9', 'D12'],
    classicalTextReference: 'BPHS Ch. 18 (Vyaya/Deshantara Bhava)',
    questions: [
      {
        id: 'foreign_settlement_promise',
        themeId: 'foreign_travel',
        questionEn: 'Is permanent foreign residency, PR, or overseas settlement promised in my chart?',
        questionHi: 'क्या मेरी कुंडली में विदेश में स्थायी निवास (PR) या नागरिकता का प्रबल योग है?',
        category: 'PROMISE',
        descriptionEn: 'Examines movable signs in 12th/1st/9th house, 4th Lord afflicted/placed in 12th, and Rahu strength',
        descriptionHi: 'चर राशियों का द्वादश/नवम में प्रभाव, चतुर्थेश का द्वादश से संबंध एवं राहु की स्थिति',
        primaryHouses: [12, 9, 4],
        secondaryHouses: [1, 7],
        primaryKarakas: ['Rahu', 'Moon'],
        secondaryKarakas: ['Saturn'],
        relevantVargas: ['D1', 'D9', 'D12'],
        timingRelevant: false
      },
      {
        id: 'foreign_timing_visa_travel',
        themeId: 'foreign_travel',
        questionEn: 'When will my visa, foreign travel, or overseas relocation materialize?',
        questionHi: 'मेरा विदेश वीजा, यात्रा या विदेश गमन का योग कब फलीभूत होगा?',
        category: 'TIMING',
        descriptionEn: 'Evaluates 12th/9th Lord Dasha, Rahu sub-period, and transits activating 12th house',
        descriptionHi: 'द्वादशेश/नवमेश की दशा, राहु की अंतर्दशा एवं द्वादश भाव पर गोचरीय ग्रहों का सक्रिय प्रभाव',
        primaryHouses: [12, 9, 3],
        secondaryHouses: [1, 10],
        primaryKarakas: ['Rahu', 'Jupiter'],
        secondaryKarakas: ['Moon'],
        relevantVargas: ['D1', 'D9'],
        timingRelevant: true
      }
    ]
  },

  litigation_competition: {
    id: 'litigation_competition',
    nameEn: 'Litigation, Court Cases & Competitions',
    nameHi: 'मुकदमेबाजी, न्यायालयीन विवाद एवं शत्रु विजय',
    iconName: 'Scale',
    descriptionEn: '6th Bhava (Shatru/Roga/Rina), Mars (Shatru Hanta), 8th Bhava, D6 & D30 diagnostics',
    descriptionHi: 'षष्ठ भाव (रिपु/विवाद), मंगल (शत्रुहंता योग), अष्टम भाव एवं कानूनी जीत का विश्लेषण',
    primaryHouses: [6, 8, 1],
    secondaryHouses: [10, 11, 12],
    naturalKarakas: ['Mars', 'Saturn', 'Sun', 'Jupiter'],
    relevantVargas: ['D1', 'D6', 'D30'],
    classicalTextReference: 'BPHS Ch. 15 (Shatru/Rina Bhava) & Saravali',
    questions: [
      {
        id: 'litigation_outcome_victory',
        themeId: 'litigation_competition',
        questionEn: 'What will be the final outcome of legal battles, court cases, or disputes?',
        questionHi: 'कोर्ट केस, कानूनी विवाद या प्रतिस्पर्धा में अंतिम परिणाम व विजय किसके पक्ष में होगी?',
        category: 'PROMISE',
        descriptionEn: 'Examines 6th Lord vs 1st Lord strength, Shatru Hanta Yoga, and 6th house BAV/SAV points',
        descriptionHi: 'षष्ठेश व लग्नेश की तुलनात्मक शक्ति, शत्रुहंता योग और षष्ठ भाव का अष्टकवर्ग बल',
        primaryHouses: [6, 1, 10],
        secondaryHouses: [8, 11],
        primaryKarakas: ['Mars', 'Sun'],
        secondaryKarakas: ['Jupiter', 'Saturn'],
        relevantVargas: ['D1', 'D30'],
        timingRelevant: false
      },
      {
        id: 'litigation_timing_resolution',
        themeId: 'litigation_competition',
        questionEn: 'When will long-standing disputes, litigation, or rivalry finally resolve?',
        questionHi: 'पुराने विवाद, मुकदमे या शत्रुता का स्थायी समाधान कब होगा?',
        category: 'TIMING',
        descriptionEn: 'Calculates Dasha transition out of 6th/8th Lord periods into 11th/1st Lord auspicious Dasha',
        descriptionHi: 'षष्ठेश/अष्टमेश की दशा समाप्ति तथा एकादशेश/लग्नेश की शुभ दशा व गोचर का समय',
        primaryHouses: [6, 11, 1],
        secondaryHouses: [8, 12],
        primaryKarakas: ['Jupiter', 'Mars'],
        secondaryKarakas: ['Saturn'],
        relevantVargas: ['D1', 'D30'],
        timingRelevant: true
      }
    ]
  },

  spirituality: {
    id: 'spirituality',
    nameEn: 'Spirituality & Moksha',
    nameHi: 'अध्यात्म, ईष्ट देव, साधना एवं मोक्ष मार्ग',
    iconName: 'Sparkles',
    descriptionEn: '9th Bhava (Dharma/Guru), 12th (Moksha), 5th (Purva Punya), D20 Vimshamsha & Atmakaraka',
    descriptionHi: 'नवम (धर्म), द्वादश (मोक्ष), पंचम (पूर्व पुण्य), विंशांश चक्र (D20) एवं आत्मकारक विश्लेषण',
    primaryHouses: [9, 12, 5],
    secondaryHouses: [1, 8, 4],
    naturalKarakas: ['Jupiter', 'Ketu', 'Sun', 'Saturn'],
    relevantVargas: ['D1', 'D9', 'D20'],
    classicalTextReference: 'BPHS Ch. 17 (Dharma/Moksha Bhava) & Jaimini Karakamsha',
    questions: [
      {
        id: 'spirituality_ishta_devata_path',
        themeId: 'spirituality',
        questionEn: 'Who is my true Ishta Devata and which spiritual/yogic path suits me best?',
        questionHi: 'मेरे सच्चे ईष्ट देव कौन हैं और मेरे लिए कौन सा आध्यात्मिक मार्ग (भक्ति, ज्ञान, कर्म, योग) श्रेष्ठ है?',
        category: 'PROMISE',
        descriptionEn: 'Calculates 12th from Karakamsha Navamsha, 9th house deities, and 5th house Mantra resonance',
        descriptionHi: 'कारकांश नवमांश से द्वादश भाव के ग्रह, नवम भाव के देवता एवं पंचम भाव के मंत्र अधिष्ठाता',
        primaryHouses: [9, 12, 5],
        secondaryHouses: [1, 8],
        primaryKarakas: ['Jupiter', 'Ketu'],
        secondaryKarakas: ['Sun', 'Moon'],
        relevantVargas: ['D1', 'D9', 'D20'],
        timingRelevant: false
      },
      {
        id: 'spirituality_timing_awakening_diksha',
        themeId: 'spirituality',
        questionEn: 'When will major spiritual awakening, Guru Diksha, or pilgrimage materialize?',
        questionHi: 'गुरु दीक्षा, आध्यात्मिक जागृति या तीर्थ यात्रा का सबसे उत्तम समय कब आएगा?',
        category: 'TIMING',
        descriptionEn: 'Evaluates 9th/12th Lord Dasha, Ketu/Jupiter sub-period, and transit across Moksha Trikona',
        descriptionHi: 'नवमेश/द्वादशेश की दशा, केतु/गुरु की अंतर्दशा और मोक्ष त्रिकोण पर शुभ गोचर का समय',
        primaryHouses: [9, 12, 5],
        secondaryHouses: [1, 8],
        primaryKarakas: ['Jupiter', 'Ketu'],
        secondaryKarakas: ['Saturn'],
        relevantVargas: ['D1', 'D20'],
        timingRelevant: true
      }
    ]
  },

  family_parents: {
    id: 'family_parents',
    nameEn: 'Family & Parents',
    nameHi: 'माता-पिता, कुल-कुटुंब एवं पैतृक सुख',
    iconName: 'Users',
    descriptionEn: '4th Bhava (Matri/Mother), 9th/10th (Pitri/Father), 2nd (Kula/Family), D12 Dwadashamsha',
    descriptionHi: 'चतुर्थ भाव (माता), नवम/दशम भाव (पिता), द्वितीय भाव (कुटुंब) एवं द्वादशांश चक्र (D12)',
    primaryHouses: [4, 9, 2],
    secondaryHouses: [10, 1, 11],
    naturalKarakas: ['Moon', 'Sun', 'Jupiter', 'Venus'],
    relevantVargas: ['D1', 'D12', 'D9'],
    classicalTextReference: 'BPHS Ch. 14, 17 & Jaimini Dwadashamsha',
    questions: [
      {
        id: 'family_parents_health_harmony',
        themeId: 'family_parents',
        questionEn: 'How will be the health, longevity, and relationship with my parents?',
        questionHi: 'माता-पिता का स्वास्थ्य, दीर्घायु एवं उनके साथ मेरे संबंध कैसे रहेंगे?',
        category: 'PROMISE',
        descriptionEn: 'Examines 4th Lord/Moon for Mother, 9th/10th Lord/Sun for Father, and D12 Dwadashamsha',
        descriptionHi: 'माता हेतु चतुर्थेश व चंद्र, पिता हेतु नवमेश व सूर्य तथा द्वादशांश चक्र (D12) का संपूर्ण अध्ययन',
        primaryHouses: [4, 9, 2],
        secondaryHouses: [10, 8],
        primaryKarakas: ['Moon', 'Sun'],
        secondaryKarakas: ['Jupiter', 'Venus'],
        relevantVargas: ['D1', 'D12'],
        timingRelevant: false
      },
      {
        id: 'family_ancestral_heritage_happiness',
        themeId: 'family_parents',
        questionEn: 'Will I receive ancestral property, family blessings, and peace in domestic life?',
        questionHi: 'क्या मुझे पैतृक संपत्ति, कुल का आशीर्वाद और पारिवारिक सुख-शांति प्राप्त होगी?',
        category: 'PROMISE',
        descriptionEn: 'Synthesizes 2nd (Kula), 8th (Inheritance), and 4th (Griha Sukha) with D12 alignment',
        descriptionHi: 'द्वितीय (कुल), अष्टम (पैतृक धन) एवं चतुर्थ (गृह सुख) भाव तथा D12 की शुभता का फलादेश',
        primaryHouses: [2, 8, 4],
        secondaryHouses: [9, 1],
        primaryKarakas: ['Jupiter', 'Mars'],
        secondaryKarakas: ['Sun', 'Moon'],
        relevantVargas: ['D1', 'D12'],
        timingRelevant: true
      }
    ]
  }
};
